import React from 'react';
import { EyeIcon, PencilIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { InventoryItem, ItemCondition } from '../../types/inventory';

interface ItemCardProps {
  item: InventoryItem;
  onView?: (item: InventoryItem) => void;
  onEdit?: (item: InventoryItem) => void;
  onMarkSold?: (item: InventoryItem) => void;
}

const ItemCard: React.FC<ItemCardProps> = ({ item, onView, onEdit, onMarkSold }) => {
  const isSold = Boolean(item.item_sales?.length);
  const getConditionColor = (condition: ItemCondition) => {
    switch (condition) {
      case 'excellent': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'very_good': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'good': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'fair': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'poor': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const formatCondition = (condition: ItemCondition) => {
    return condition.replace('_', ' ').toUpperCase();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      {/* Item Image */}
      <div className="aspect-square bg-gray-100 dark:bg-gray-700 relative">
        {item.images.length > 0 ? (
          <img
            src={item.images[0]}
            alt={item.item_name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <EyeIcon className="h-12 w-12" />
          </div>
        )}

        {/* Condition Badge */}
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getConditionColor(item.condition)}`}>
            {formatCondition(item.condition)}
          </span>
        </div>
        {isSold && (
          <div className="absolute top-2 right-2">
            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-600 text-white">
              SOLD
            </span>
          </div>
        )}
      </div>

      {/* Item Details */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
          {item.item_name}
        </h3>

        {/* Price */}
        {item.suggested_price && (
          <div className="flex items-center gap-1 mb-2">
            <CurrencyDollarIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
            <span className="text-lg font-bold text-green-600 dark:text-green-400">
              ${item.suggested_price.toFixed(2)}
            </span>
          </div>
        )}

        {/* Description */}
        {item.item_description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
            {item.item_description}
          </p>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => onView?.(item)}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm"
          >
            <EyeIcon className="h-4 w-4" />
            View
          </button>
          <button
            onClick={() => onEdit?.(item)}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors text-sm"
          >
            <PencilIcon className="h-4 w-4" />
            Edit
          </button>
        </div>

        {/* Mark as Sold Button */}
        {onMarkSold && !isSold && (
          <button
            onClick={() => onMarkSold(item)}
            className="w-full mt-2 flex items-center justify-center gap-1 px-3 py-2 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-800 transition-colors text-sm"
          >
            <CurrencyDollarIcon className="h-4 w-4" />
            Mark Sold
          </button>
        )}
      </div>
    </div>
  );
};

export default ItemCard;
